import styled, { css } from "styled-components";
import { Page } from "../../../components/layout/page/page.component";
import { Form, Select, Switch } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import { Settings } from "../../../models/settings.model";
import { optionify } from "../../../utils/functions/misc/optionify";
import { SubmitButton } from "../../../components/layout/submit-button/submit-button.component";
import { useAtomValue } from "jotai";
import { authAtom } from "../../../atoms/auth.atom";
import { useCallback, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { handleResponse } from "../../../utils/functions/http/handleResponse";
import toast from "react-hot-toast";
import { axios } from "../../../api/axios";
import { Column } from "../../../components/ui/grid/column.component";
import { Card } from "../../../components/ui/card/card.component";
import { colors } from "../../../theme/colors";
import {
  MdDarkMode,
  MdLightMode,
  MdMail,
  MdSms,
  MdTune,
  MdCheckCircle,
} from "react-icons/md";

export const SettingsPage = () => {
  const [hasSetForm, setHasSetForm] = useState(false);
  const auth = useAtomValue(authAtom);

  const [form] = useForm<Settings>();
  const darkMode = useWatch("dark_mode", form);

  useEffect(() => {
    if (auth.user && !hasSetForm) {
      form.setFieldsValue({
        subscribe_to_newsletters: auth.user.subscribe_to_newsletters,
        subscribe_to_mobile_notifications:
          auth.user.subscribe_to_mobile_notifications,
        dark_mode: auth.user.dark_mode ?? true,
      });
      setHasSetForm(true);
    }
  }, [form, auth, hasSetForm]);

  const onSubmit = useCallback(async () => {
    try {
      await form.validateFields();

      const profile = { ...form.getFieldsValue() };
      if (!profile.default_module) profile.default_module = "play";

      const formData = new FormData();
      for (const key in profile) {
        const typedKey = key as keyof typeof profile;
        formData.append(key, String(profile[typedKey]));
      }
      if (auth.user?.profile_photo)
        formData.append("profile_photo", auth.user?.profile_photo);

      axios
        .put("/auth/update/user/profile", formData)
        .then((resp) =>
          handleResponse(resp, () => {
            window.location.reload();
          })
        )
        .catch(handleResponse);
    } catch (e) {
      if (e instanceof AxiosError) {
        handleResponse(e);
      } else {
        toast.error("Please fix the errors in the form!");
      }
    }
  }, [form, auth]);

  const setTheme = (value: boolean) =>
    form.setFieldValue("dark_mode", value);

  return (
    <Page>
      <Main>
        <Header>
          <Title>Settings</Title>
          <Subtitle>
            Tailor your experience — appearance, notifications, and defaults.
          </Subtitle>
        </Header>

        <Form form={form} layout="vertical" style={{ width: "100%" }}>
          <SectionCard>
            <SectionHeader>
              <SectionTitle>Appearance</SectionTitle>
              <SectionHint>
                Pick a theme. Click a preview to apply it.
              </SectionHint>
            </SectionHeader>

            <Form.Item name="dark_mode" valuePropName="checked" noStyle>
              <input type="hidden" />
            </Form.Item>

            <ThemeGrid>
              <ThemePreview
                $active={darkMode === true}
                $dark
                onClick={() => setTheme(true)}
                role="button"
                aria-pressed={darkMode === true}
              >
                <PreviewMockup $dark>
                  <PreviewSidebar $dark>
                    <PreviewLogoDot />
                    <PreviewNavItem $active />
                    <PreviewNavItem />
                    <PreviewNavItem />
                  </PreviewSidebar>
                  <PreviewContent $dark>
                    <PreviewBar $w={60} />
                    <PreviewBar $w={90} $muted />
                    <PreviewBar $w={75} $muted />
                    <PreviewCardRow>
                      <PreviewMiniCard $dark />
                      <PreviewMiniCard $dark />
                    </PreviewCardRow>
                  </PreviewContent>
                </PreviewMockup>
                <PreviewLabel>
                  <PreviewLabelLeft>
                    <MdDarkMode size={20} />
                    <span>Dark Mode</span>
                  </PreviewLabelLeft>
                  {darkMode === true && <MdCheckCircle size={20} />}
                </PreviewLabel>
              </ThemePreview>

              <ThemePreview
                $active={darkMode === false}
                onClick={() => setTheme(false)}
                role="button"
                aria-pressed={darkMode === false}
              >
                <PreviewMockup>
                  <PreviewSidebar>
                    <PreviewLogoDot />
                    <PreviewNavItem $active />
                    <PreviewNavItem />
                    <PreviewNavItem />
                  </PreviewSidebar>
                  <PreviewContent>
                    <PreviewBar $w={60} />
                    <PreviewBar $w={90} $muted />
                    <PreviewBar $w={75} $muted />
                    <PreviewCardRow>
                      <PreviewMiniCard />
                      <PreviewMiniCard />
                    </PreviewCardRow>
                  </PreviewContent>
                </PreviewMockup>
                <PreviewLabel>
                  <PreviewLabelLeft>
                    <MdLightMode size={20} />
                    <span>Light Mode</span>
                  </PreviewLabelLeft>
                  {darkMode === false && <MdCheckCircle size={20} />}
                </PreviewLabel>
              </ThemePreview>
            </ThemeGrid>
          </SectionCard>

          <SectionCard>
            <SectionHeader>
              <SectionTitle>Notifications</SectionTitle>
              <SectionHint>
                Choose how you'd like us to keep you in the loop.
              </SectionHint>
            </SectionHeader>

            <ToggleList>
              <ToggleRow>
                <ToggleIcon>
                  <MdMail size={22} />
                </ToggleIcon>
                <ToggleText>
                  <ToggleTitle>Email Notifications</ToggleTitle>
                  <ToggleDescription>
                    Product updates, newsletters, and important account emails.
                  </ToggleDescription>
                </ToggleText>
                <ToggleControl
                  name="subscribe_to_newsletters"
                  valuePropName="checked"
                >
                  <Switch />
                </ToggleControl>
              </ToggleRow>

              <ToggleDivider />

              <ToggleRow>
                <ToggleIcon>
                  <MdSms size={22} />
                </ToggleIcon>
                <ToggleText>
                  <ToggleTitle>SMS Notifications</ToggleTitle>
                  <ToggleDescription>
                    Time-sensitive alerts sent to your registered mobile number.
                  </ToggleDescription>
                </ToggleText>
                <ToggleControl
                  name="subscribe_to_mobile_notifications"
                  valuePropName="checked"
                >
                  <Switch />
                </ToggleControl>
              </ToggleRow>
            </ToggleList>
          </SectionCard>

          <SectionCard>
            <SectionHeader>
              <SectionTitle>Preferences</SectionTitle>
              <SectionHint>
                Where you'd like to land each time you sign in.
              </SectionHint>
            </SectionHeader>

            <PreferenceRow>
              <ToggleIcon>
                <MdTune size={22} />
              </ToggleIcon>
              <ToggleText>
                <ToggleTitle>Default Module</ToggleTitle>
                <ToggleDescription>
                  We'll open this module by default when you log in.
                </ToggleDescription>
              </ToggleText>
              <PreferenceControl name="default_module">
                <Select
                  options={optionify(["Play", "Distribute", "Festivals"])}
                  defaultValue={"play"}
                  showSearch
                  placeholder="Select default module"
                />
              </PreferenceControl>
            </PreferenceRow>
          </SectionCard>
        </Form>
      </Main>

      <SubmitButton label="Save Settings" onClick={onSubmit} />
    </Page>
  );
};

const Main = styled(Column).attrs({
  $full: true,
})`
  align-items: stretch;
  padding: 0 0 12rem 0;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 0.8rem;

  @media (max-width: 48em) {
    margin-top: 4rem;
  }
`;

const Title = styled.h1`
  font-size: 3.2rem;
  font-weight: 600;
  margin: 0;
  color: ${colors.text.hex};
`;

const Subtitle = styled.p`
  font-size: 1.4rem;
  color: ${colors.text.withOpacity(0.6).hex};
  margin: 0;
`;

const SectionCard = styled(Card)`
  padding: 2rem 2.4rem;
  border: 1.5px solid ${colors.primary.withOpacity(0.3).hex};
  width: 100%;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1.6rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${colors.grey.hex};
`;

const SectionTitle = styled.h3`
  font-size: 1.7rem;
  font-weight: 600;
  margin: 0;
  color: ${colors.text.hex};
`;

const SectionHint = styled.span`
  font-size: 1.25rem;
  color: ${colors.text.withOpacity(0.6).hex};
  line-height: 1.4;
`;

const ThemeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2rem;

  @media (max-width: 48em) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const ThemePreview = styled.div<{ $active?: boolean; $dark?: boolean }>`
  border-radius: 1rem;
  border: 2px solid
    ${({ $active }) =>
      $active ? colors.primary.hex : colors.primary.withOpacity(0.15).hex};
  background: ${({ $dark }) =>
    $dark ? "#07121d" : "#ffffff"};
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  box-shadow: ${({ $active }) =>
    $active
      ? `0 0 0 4px ${colors.primary.withOpacity(0.15).hex}`
      : "none"};

  &:hover {
    transform: translateY(-0.2rem);
    border-color: ${colors.primary.hex};
  }
`;

const PreviewMockup = styled.div<{ $dark?: boolean }>`
  display: flex;
  height: 14rem;
  background: ${({ $dark }) => ($dark ? "#040C15" : "#ffffff")};
  border-bottom: 1px solid
    ${({ $dark }) => ($dark ? "rgba(242,247,252,0.08)" : "#DEE0E4")};
`;

const PreviewSidebar = styled.div<{ $dark?: boolean }>`
  width: 5rem;
  padding: 1rem 0.6rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.7rem;
  background: ${({ $dark }) =>
    $dark ? "rgba(242,247,252,0.04)" : "rgba(4,12,21,0.04)"};
  border-right: 1px solid
    ${({ $dark }) => ($dark ? "rgba(242,247,252,0.08)" : "#DEE0E4")};
`;

const PreviewLogoDot = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background: ${colors.primary.hex};
  margin-bottom: 0.5rem;
`;

const PreviewNavItem = styled.div<{ $active?: boolean }>`
  width: 2.6rem;
  height: 0.6rem;
  border-radius: 0.3rem;
  background: ${({ $active }) =>
    $active ? colors.primary.hex : "rgba(128,128,128,0.4)"};
`;

const PreviewContent = styled.div<{ $dark?: boolean }>`
  flex: 1;
  padding: 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;

  ${({ $dark }) =>
    $dark
      ? css`
          color: #f2f7fc;
        `
      : css`
          color: #040c15;
        `}
`;

const PreviewBar = styled.div<{ $w: number; $muted?: boolean }>`
  height: 0.7rem;
  width: ${({ $w }) => $w}%;
  border-radius: 0.3rem;
  background: ${({ $muted }) =>
    $muted ? "rgba(128,128,128,0.35)" : colors.primary.hex};
`;

const PreviewCardRow = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-top: 0.4rem;
  flex: 1;
`;

const PreviewMiniCard = styled.div<{ $dark?: boolean }>`
  flex: 1;
  border-radius: 0.5rem;
  background: ${({ $dark }) =>
    $dark ? "rgba(242,247,252,0.06)" : "rgba(4,12,21,0.05)"};
  border: 1px solid
    ${({ $dark }) => ($dark ? "rgba(242,247,252,0.1)" : "rgba(4,12,21,0.08)")};
`;

const PreviewLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.4rem;
  background: ${colors.background.hex};
  color: ${colors.text.hex};
  font-size: 1.4rem;
  font-weight: 600;
  border-top: 1px solid ${colors.grey.withOpacity(0.5).hex};

  & svg:last-child {
    color: ${colors.primary.hex};
  }
`;

const PreviewLabelLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const ToggleList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.4rem;
  padding: 1.2rem 0;

  @media (max-width: 36em) {
    flex-wrap: wrap;
  }
`;

const ToggleDivider = styled.div`
  height: 1px;
  background: ${colors.grey.withOpacity(0.5).hex};
`;

const ToggleIcon = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.primary.withOpacity(0.1).hex};
  color: ${colors.primary.hex};
  flex-shrink: 0;
`;

const ToggleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 1;
  min-width: 0;
`;

const ToggleTitle = styled.span`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${colors.text.hex};
`;

const ToggleDescription = styled.span`
  font-size: 1.25rem;
  color: ${colors.text.withOpacity(0.6).hex};
  line-height: 1.4;
`;

const ToggleControl = styled(Form.Item)`
  margin: 0;
  flex-shrink: 0;

  .ant-form-item-control-input {
    min-height: auto;
  }
`;

const PreferenceRow = styled(ToggleRow)`
  padding: 0.4rem 0;

  @media (max-width: 36em) {
    align-items: flex-start;
  }
`;

const PreferenceControl = styled(Form.Item)`
  margin: 0;
  width: 22rem;
  flex-shrink: 0;

  .ant-select {
    width: 100%;
  }

  .ant-select-selector {
    font-size: 1.4rem;
  }

  @media (max-width: 36em) {
    width: 100%;
  }
`;
